<template lang="pug">
.calendarComponent(
  ref="root"
  :class="componentClasses"
)
  .field(@click="openCalendar")
    input.input(
      ref="input"
      type="text"
      :value="displayValue"
      :placeholder="placeholderText"
      :disabled="isDisabled"
      readonly
      @focus="openCalendar"
    )
    .icon

  .dropdown(
    v-if="isOpen && !isDisabled"
  )
    .header
      button.nav(
        type="button"
        @click.stop="prev"
      ) ‹
      .title
        template(v-if="calendarView === 'day'")
          span.month(@click.stop="openMonthView") {{ monthNames[viewDate.getMonth()] }} 
          span.year(@click.stop="openYearView") {{ viewDate.getFullYear() }}

        template(v-else-if="calendarView === 'month'")
          span.year(@click.stop="openYearView") {{ viewDate.getFullYear() }}

        template(v-else)
          span {{ yearPage }}-{{ yearPage + 19 }}
      button.nav(
        type="button"
        @click.stop="next"
      ) ›

    template(v-if="calendarView === 'day'")
      .weekdays
        .weekday(
          v-for="dayName in weekDays"
          :key="dayName"
        ) {{ dayName }}

      .grid
        button.day(
          v-for="day in calendarDays"
          :key="day.key"
          type="button"
          :class="getDayClasses(day)"
          :disabled="day.isDisabled"
          @click.stop="selectDay(day)"
        ) {{ day.date.getDate() }}

    .gridMonths(v-else-if="calendarView === 'month'")
      button.monthItem(
        v-for="(m, i) in monthShort"
        :key="m"
        type="button"
        @click.stop="selectMonth(i)"
      ) {{ m }}

    .gridYears(v-else-if="calendarView === 'year'")
      button.yearItem(
        v-for="y in yearRange"
        :key="y"
        type="button"
        @click.stop="selectYear(y)"
      ) {{ y }}

    .footer
      button.action(
        type="button"
        @click.stop="selectToday"
      ) Сегодня
      button.action(
        type="button"
        @click.stop="clearValue"
      ) Очистить
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { ValidationStatus } from '../types'

interface Props {
  modelValue: string | null
  placeholderText?: string
  variant?: 'default' | 'outlined' | 'gray' | 'white'
  isDisabled?: boolean
  validationStatus?: ValidationStatus | null
  min?: string
  max?: string
}

interface CalendarDay {
  key: string
  date: Date
  isCurrentMonth: boolean
  isSelected: boolean
  isToday: boolean
  isDisabled: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  placeholderText: 'ДД.ММ.ГГГГ',
  variant: 'default',
  isDisabled: false,
  validationStatus: ValidationStatus.None,
  min: undefined,
  max: undefined
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | null): void
  (e: 'blur'): void
}>()

const root = ref<HTMLElement | null>(null)
const input = ref<HTMLInputElement | null>(null)
const isOpen = ref(false)
const openDirection = ref<'bottom' | 'top'>('bottom')
type CalendarView = 'day' | 'month' | 'year'
const calendarView = ref<CalendarView>('day')

const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
const monthNames = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь'
]

const monthShort = [
  'Янв',
  'Фев',
  'Мар',
  'Апр',
  'Май',
  'Июн',
  'Июл',
  'Авг',
  'Сен',
  'Окт',
  'Ноя',
  'Дек'
]

const componentClasses = computed(() => [
  `_${props.variant}`,
  {
    _disabled: props.isDisabled,
    _open: isOpen.value,
    _top: openDirection.value === 'top',
    _valid: props.validationStatus === ValidationStatus.Valid,
    _invalid: props.validationStatus === ValidationStatus.Invalid
  }
])

const parseModelValue = (value: string | null): Date | null => {
  if (!value) return null

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null

  const year = Number(match[1])
  const month = Number(match[2]) - 1
  const day = Number(match[3])

  const date = new Date(year, month, day)

  if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
    return null
  }

  return date
}

const updateDropdownPosition = () => {
  if (!root.value || !isOpen.value) return

  const rect = root.value.getBoundingClientRect()

  const dropdownHeight = 320
  const spaceBelow = window.innerHeight - rect.bottom
  const spaceAbove = rect.top

  if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
    openDirection.value = 'top'
  } else {
    openDirection.value = 'bottom'
  }
}

const formatModelValue = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const formatDisplayValue = (value: string | null): string => {
  const date = parseModelValue(value)
  if (!date) return ''

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()

  return `${day}.${month}.${year}`
}

const normalizeDate = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate())

const selectedDate = computed(() => parseModelValue(props.modelValue))
const displayValue = computed(() => formatDisplayValue(props.modelValue))

const viewDate = ref<Date>(selectedDate.value ?? normalizeDate(new Date()))

const yearPage = ref(Math.floor(viewDate.value.getFullYear() / 20) * 20)

const yearRange = computed(() => {
  return Array.from({ length: 20 }, (_, i) => yearPage.value + i)
})

watch(
  () => props.modelValue,
  value => {
    const parsed = parseModelValue(value)
    if (parsed) {
      viewDate.value = new Date(parsed.getFullYear(), parsed.getMonth(), 1)
    }
  }
)

const monthTitle = computed(() => {
  return `${monthNames[viewDate.value.getMonth()]} ${viewDate.value.getFullYear()}`
})

const minDate = computed(() => {
  const parsed = parseModelValue(props.min ?? null)
  return parsed ? normalizeDate(parsed) : null
})

const maxDate = computed(() => {
  const parsed = parseModelValue(props.max ?? null)
  return parsed ? normalizeDate(parsed) : null
})

const isSameDate = (a: Date | null, b: Date | null): boolean => {
  if (!a || !b) return false

  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

const isDateDisabled = (date: Date): boolean => {
  const normalized = normalizeDate(date)

  if (minDate.value && normalized < minDate.value) return true
  if (maxDate.value && normalized > maxDate.value) return true

  return false
}

const getMonthGridStart = (date: Date): Date => {
  const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1)
  const jsDay = firstDayOfMonth.getDay()
  const mondayBasedDay = jsDay === 0 ? 7 : jsDay
  const offset = mondayBasedDay - 1

  return new Date(
    firstDayOfMonth.getFullYear(),
    firstDayOfMonth.getMonth(),
    firstDayOfMonth.getDate() - offset
  )
}

const calendarDays = computed<CalendarDay[]>(() => {
  const start = getMonthGridStart(viewDate.value)
  const today = normalizeDate(new Date())
  const result: CalendarDay[] = []

  for (let i = 0; i < 42; i += 1) {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)

    result.push({
      key: `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`,
      date,
      isCurrentMonth: date.getMonth() === viewDate.value.getMonth(),
      isSelected: isSameDate(date, selectedDate.value),
      isToday: isSameDate(date, today),
      isDisabled: isDateDisabled(date)
    })
  }

  return result
})

const openCalendar = () => {
  if (props.isDisabled) return

  calendarView.value = 'day'
  isOpen.value = true

  requestAnimationFrame(() => {
    updateDropdownPosition()
  })

  if (selectedDate.value) {
    viewDate.value = new Date(selectedDate.value.getFullYear(), selectedDate.value.getMonth(), 1)
  }
}

const closeCalendar = () => {
  isOpen.value = false
  emit('blur')
}

const prev = () => {
  if (calendarView.value === 'day') {
    viewDate.value = new Date(viewDate.value.getFullYear(), viewDate.value.getMonth() - 1, 1)
  } else if (calendarView.value === 'month') {
    viewDate.value = new Date(viewDate.value.getFullYear() - 1, viewDate.value.getMonth(), 1)
  } else {
    yearPage.value -= 20
  }
}

const next = () => {
  if (calendarView.value === 'day') {
    viewDate.value = new Date(viewDate.value.getFullYear(), viewDate.value.getMonth() + 1, 1)
  } else if (calendarView.value === 'month') {
    viewDate.value = new Date(viewDate.value.getFullYear() + 1, viewDate.value.getMonth(), 1)
  } else {
    yearPage.value += 20
  }
}

const selectDay = (day: CalendarDay) => {
  if (day.isDisabled) return

  emit('update:modelValue', formatModelValue(day.date))
  isOpen.value = false
  emit('blur')
}

const selectToday = () => {
  const today = normalizeDate(new Date())
  if (isDateDisabled(today)) return

  emit('update:modelValue', formatModelValue(today))
  viewDate.value = new Date(today.getFullYear(), today.getMonth(), 1)
  isOpen.value = false
  emit('blur')
}

const clearValue = () => {
  emit('update:modelValue', null)
  isOpen.value = false
  emit('blur')
}

const selectMonth = (month: number) => {
  viewDate.value = new Date(viewDate.value.getFullYear(), month, 1)
  calendarView.value = 'day'
}

const openMonthView = () => {
  calendarView.value = 'month'
}

const selectYear = (year: number) => {
  viewDate.value = new Date(year, viewDate.value.getMonth(), 1)
  calendarView.value = 'month'
}

const openYearView = () => {
  yearPage.value = Math.floor(viewDate.value.getFullYear() / 20) * 20
  calendarView.value = 'year'
}

const getDayClasses = (day: CalendarDay) => [
  {
    _muted: !day.isCurrentMonth,
    _selected: day.isSelected,
    _today: day.isToday
  }
]

const onDocumentClick = (event: MouseEvent) => {
  const target = event.target as Node | null
  if (!root.value || !target) return

  if (!root.value.contains(target)) {
    isOpen.value = false
    emit('blur')
  }
}

const onDocumentKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    isOpen.value = false
    emit('blur')
  }
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onDocumentKeydown)

  window.addEventListener('scroll', updateDropdownPosition, true)
  window.addEventListener('resize', updateDropdownPosition)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onDocumentKeydown)

  window.removeEventListener('scroll', updateDropdownPosition, true)
  window.removeEventListener('resize', updateDropdownPosition)
})

defineExpose({ input })
</script>

<style lang="sass" scoped>
.calendarComponent
  position: relative

  > .field
    position: relative
    cursor: pointer

    > .icon
      position: absolute
      top: 50%
      right: 12px
      transform: translateY(-50%)
      width: 18px
      height: 18px
      pointer-events: none

      &::before
        content: '🗓️'
        display: block
        font-size: 22px
        line-height: 18px
        text-align: center

  > .field > .input
    border: 1px solid transparent
    display: block
    width: 100%
    padding: 2.25*$u
    padding-right: 10*$u
    @include font(t14)
    border-radius: $brXS
    cursor: pointer

  > .dropdown
    position: absolute
    left: 0
    top: calc(100% + 4px)
    z-index: 100
    width: 100%
    min-width: 280px
    max-width: 360px
    box-sizing: border-box
    padding: 3*$u
    border-radius: $brXS
    background: $colorWhite
    border: 0.25*$u solid $colorGray
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08)

    > .header
      display: flex
      align-items: center
      justify-content: space-between
      margin-bottom: 3*$u

      > .title
        @include font(t14-demibold)
        display: flex
        align-items: center
        justify-content: center
        gap: 6px

        > .month,
        > .year
          cursor: pointer

      > .nav
        border: none
        background: transparent
        cursor: pointer
        width: 32px
        height: 32px
        border-radius: 8px
        @include font(t16-demibold)

    > .weekdays
      display: grid
      grid-template-columns: repeat(7, 1fr)
      gap: $u
      margin-bottom: $u

      > .weekday
        text-align: center
        @include font(t12)
        color: $colorPrimary

      > .weekday:nth-child(7)
          color: $colorDanger

    > .grid
      display: grid
      grid-template-columns: repeat(7, 1fr)
      gap: $u

      > .day
        height: 36px
        border: none
        border-radius: 8px
        background: transparent
        cursor: pointer
        @include font(t14)

        &._muted
          opacity: .45

        &._today
          outline: 1px solid $colorPrimary

        &._selected
          background: $colorPrimary
          color: $colorWhite

        &:disabled
          opacity: .3
          cursor: not-allowed

      > .day:nth-child(7n)
        color: $colorDanger

    > .gridMonths
      display: grid
      grid-template-columns: repeat(3, 1fr)
      gap: $u

      > .monthItem
        height: 40px
        border: none
        border-radius: 8px
        background: transparent
        cursor: pointer
        @include font(t14)

    > .gridYears
      display: grid
      grid-template-columns: repeat(4, 1fr)
      gap: $u

      > .yearItem
        height: 36px
        border: none
        border-radius: 8px
        background: transparent
        cursor: pointer
        @include font(t14)

    > .footer
      display: flex
      justify-content: space-between
      gap: 2*$u
      margin-top: 3*$u

      > .action
        border: none
        background: transparent
        cursor: pointer
        color: $colorPrimary
        @include font(t14)

  &._top
    > .dropdown
      top: auto
      bottom: calc(100% + 4px)

  &._default
    > .field > .input
      background: $colorBackground2

  &._gray
    > .field > .input
      background: $colorBackground

  &._white
    > .field > .input
      background: $colorWhite

  &._outlined
    > .field > .input
      border: 0.25*$u solid $colorGray
      background: $colorWhite

  &._disabled
    > .field
      cursor: not-allowed

    > .field > .input
      cursor: not-allowed

  &._valid
    > .field > .input
      background: $colorCalendar3

  &._invalid
    > .field > .input
      background: $colorCalendar1

  @media (max-width: 767px)
    > .dropdown
      width: 100%
      min-width: 0
      max-width: none
      padding: 2*$u

      > .header
        margin-bottom: 2*$u

        > .title
          text-align: center

      > .weekdays
        gap: 0.5*$u

      > .grid
        gap: 0.5*$u

        > .day
          height: 32px
          padding: 0
          font-size: 14px

      > .footer
        margin-top: 2*$u
</style>
